import { ARMY, ARMY_SIZE, CELLS, COLS, FLAG, GRUNT, POWER, RANKS, ROWS, SPY, cellAt, homeZone, inHome, isRank, neighbours, pieceAt, } from './pieces.js';
/**
 * THE ARBITER. `mover` moved onto the square, `holder` was standing on it.
 *
 * The straight POWER chain decides most of it, and the three exceptions on top
 * of it are the entire game — get one backwards and Gera becomes checkers with
 * extra steps:
 *
 *  1. A tiktik beats every opisyal and heneral, from either side of the clash.
 *  2. …but a tanod beats a tiktik, and a tiktik loses to a tanod. This pair
 *     overrides the chain in BOTH directions, which is why it is tested before
 *     rule 1: on the bare chain the tanod (2) already outranks the tiktik (1),
 *     and rule 1 as written would otherwise steal the clash back.
 *  3. A watawat loses to everything — EXCEPT that a watawat moving onto the
 *     enemy watawat captures it. The flag that moved wins; the flag that sat
 *     still loses. It is the one place in the game where being the attacker is
 *     worth more than a rank.
 *
 * Equal ranks are a mutual loss. Note that this can never be flag-vs-flag —
 * exception 3 catches that pair first — so "both fell" always means two live
 * pieces of the same grade traded off.
 */
export function resolveClash(mover, holder) {
    // (3) The moving flag captures the sitting flag.
    if (mover === FLAG && holder === FLAG)
        return 'mover';
    // (2) The tanod/tiktik pair, before anything else can claim it.
    if (mover === SPY && holder === GRUNT)
        return 'holder';
    if (mover === GRUNT && holder === SPY)
        return 'mover';
    // (1) Otherwise a tiktik beats whatever it meets, attacking or defending.
    if (mover === SPY && holder !== SPY)
        return 'mover';
    if (holder === SPY && mover !== SPY)
        return 'holder';
    const pm = POWER[mover];
    const ph = POWER[holder];
    if (pm === ph)
        return 'both';
    return pm > ph ? 'mover' : 'holder';
}
/**
 * Every legal step for a seat, in board order (lowest origin first, then up /
 * down / left / right per neighbours()).
 *
 * Depends on OCCUPANCY only — never on a rank. That is not a coincidence and it
 * is load-bearing: it means a seat's move list, and the "no legal move" loss,
 * are functions of public information, so handing a seat its own legal list
 * discloses nothing about anybody's ranks.
 */
export function movesFor(board, seat) {
    const out = [];
    for (let from = 0; from < CELLS; from++) {
        const pc = pieceAt(board, from);
        if (!pc || pc.seat !== seat)
            continue;
        for (const to of neighbours(from)) {
            const target = pieceAt(board, to);
            if (!target || target.seat !== seat)
                out.push({ from, to });
        }
    }
    return out;
}
export function hasLegalMove(board, seat) {
    for (let from = 0; from < CELLS; from++) {
        const pc = pieceAt(board, from);
        if (!pc || pc.seat !== seat)
            continue;
        for (const to of neighbours(from)) {
            const target = pieceAt(board, to);
            if (!target || target.seat !== seat)
                return true;
        }
    }
    return false;
}
/**
 * The repetition key: OCCUPANCY plus the seat to move. `.` empty, `0`/`1` the
 * owner, then `|` and the seat on the clock.
 *
 * DIVERGENCE from the reference, which hashed the ranks into this key. Two
 * reasons. Ranks cannot change without a clash, and a clash resets the
 * no-progress counter anyway, so within any stretch that could reach a draw the
 * occupancy IS the position. And keeping the ledger rank-free means the draw is
 * something both players can verify from what they were shown, that a state
 * snapshot carries no secret, and that a counterfactual reassignment of hidden
 * ranks leaves the ledger coherent instead of stale.
 */
export function occupancyKey(board, turn) {
    let key = '';
    for (let i = 0; i < CELLS; i++) {
        const pc = pieceAt(board, i);
        key += pc === null ? '.' : String(pc.seat);
    }
    return `${key}|${turn}`;
}
/** Offered in the order the HUD lists them; the first is the timeout default. */
export const FORMATION_NAMES = ['harang', 'kuta', 'sugod'];
const __ = null;
/**
 * The three presets, each written as 27 local cells (3 rows × 9 cols) with six
 * squares left empty.
 *
 * LOCAL ROW 0 IS THE SEAT'S OWN BACK EDGE — the row furthest from the enemy,
 * where a guarded watawat sits — and local row 2 faces the centre. Both seats
 * therefore mirror each other through expandFormation(), which is what makes a
 * preset mean the same thing whichever chair you are in.
 */
export const FORMATIONS = {
    /** The screen: flag mid-back between both tiktik, tanod spread across the front. */
    harang: [
        'tanod', 'watawat', 'tiktik', 'heneral-1', 'opisyal-7', 'heneral-2', 'tiktik', 'tanod', __,
        'heneral-5', 'opisyal-6', 'opisyal-5', 'tanod', 'opisyal-4', 'tanod', 'opisyal-3', 'opisyal-2', 'heneral-4',
        __, 'tanod', 'opisyal-1', 'heneral-3', 'tanod', __, __, __, __,
    ],
    /** The fort: flag jammed into a back corner behind a wall of tanod. */
    kuta: [
        'watawat', 'tanod', 'tanod', 'tiktik', __, __, 'heneral-2', 'opisyal-7', 'opisyal-6',
        'tanod', 'heneral-1', 'opisyal-5', 'opisyal-4', 'tiktik', 'opisyal-3', 'heneral-5', 'heneral-4', 'heneral-3',
        __, 'opisyal-2', 'opisyal-1', 'tanod', 'tanod', __, 'tanod', __, __,
    ],
    /** The charge: every heneral on the front row, flag deep and lightly held. */
    sugod: [
        __, __, 'tanod', 'tiktik', 'watawat', 'tiktik', 'tanod', __, __,
        'opisyal-1', 'opisyal-2', 'opisyal-3', 'opisyal-7', 'opisyal-6', 'opisyal-5', 'opisyal-4', 'tanod', 'tanod',
        __, 'heneral-1', 'heneral-2', 'heneral-3', 'heneral-4', 'heneral-5', 'tanod', 'tanod', __,
    ],
};
/**
 * A preset as concrete placements for one seat, sorted by square.
 *
 * Deliberately NOT seeded. The reference mirrored its templates on a coin flip
 * for bot variety; here the presets are what a timed-out seat gets and what a
 * vector asserts, so they have to read the same every time. A player who wants
 * variety drags their own layout — that is the whole setup screen.
 */
export function expandFormation(seat, name) {
    const local = FORMATIONS[name];
    const out = [];
    for (let lr = 0; lr < 3; lr++) {
        for (let c = 0; c < COLS; c++) {
            const rank = local[lr * COLS + c];
            if (!rank)
                continue;
            // Local row 0 is each seat's own back edge: row 0 for seat 0, row 7 for
            // seat 1. Columns are never flipped, so the two armies face each other.
            out.push({ at: cellAt(seat === 0 ? lr : ROWS - 1 - lr, c), rank });
        }
    }
    return out.sort((a, b) => a.at - b.at);
}
/**
 * Why a deployment is illegal, or null when it is fine. Returns Taglish because
 * the string goes straight to the HUD.
 *
 * Checks, in the order a client is most likely to get them wrong: the count,
 * then the zone, then overlaps, then the army itself. The multiset check is
 * exact — a 21-piece layout with two watawat and no tiktik passes every other
 * test and would be a different game.
 */
export function deployProblem(board, seat, placements) {
    // parseAction() guarantees an array, but apply() is reachable without it (a raw
    // socket payload, a replay tool, a caller from a newer build). A missing or
    // non-array `placements` has to come back as a verdict, not a TypeError: every
    // caller reads `.ok` off the result and an exception escapes that contract.
    if (!Array.isArray(placements))
        return 'Hindi mabasa ang paglalatag mo.';
    if (placements.length !== ARMY_SIZE) {
        return `Dapat eksaktong ${ARMY_SIZE} piyesa ang ilalatag mo.`;
    }
    const zone = homeZone(seat);
    const taken = new Set();
    const counts = new Map();
    for (const placement of placements) {
        // Same reason as the Array check above: a hand-built payload can carry a null
        // or a bare number in the list, and destructuring one throws.
        if (placement === null || typeof placement !== 'object') {
            return 'Hindi mabasa ang paglalatag mo.';
        }
        const { at, rank } = placement;
        if (!isRank(rank))
            return 'Hindi mabasa ang paglalatag mo.';
        if (!inHome(seat, at)) {
            return `Sa mga hanay mo lang (${zone.from}–${zone.to}) pwedeng maglatag.`;
        }
        if (taken.has(at))
            return 'May dalawang piyesa sa iisang parisukat.';
        if (pieceAt(board, at) !== null)
            return 'May nakatayo na sa parisukat na ’yan.';
        taken.add(at);
        counts.set(rank, (counts.get(rank) ?? 0) + 1);
    }
    for (const rank of RANKS) {
        if ((counts.get(rank) ?? 0) !== ARMY[rank])
            return `Mali ang bilang ng ${rank} mo.`;
    }
    return null;
}
//# sourceMappingURL=rules.js.map